import { useEffect, useState, type RefObject } from 'react'
import { AVATAR_DURATION, AVATAR_MIDPOINT } from './useAvatarScrub'

export type MobileHeroPhase =
  | 'idle'
  | 'left'
  | 'turning-right'
  | 'right'
  | 'turning-center'
  | 'center'
  | 'turning-left'

export const MOBILE_ICON_PHASE_DURATION = 5_000
export const MOBILE_CENTER_PHASE_DURATION = 10_000
export const MOBILE_TURN_DURATION = 1_000

interface MobileHeroSequenceOptions {
  videoRef: RefObject<HTMLVideoElement | null>
  targetRef: RefObject<HTMLElement | null>
  enabled: boolean
}

function easeInOutCubic(progress: number) {
  return progress < 0.5
    ? 4 * progress * progress * progress
    : 1 - Math.pow(-2 * progress + 2, 3) / 2
}

export function useMobileHeroSequence({ videoRef, targetRef, enabled }: MobileHeroSequenceOptions) {
  const [phase, setPhase] = useState<MobileHeroPhase>('idle')

  useEffect(() => {
    if (!enabled) return

    const video = videoRef.current
    const target = targetRef.current
    if (!video || !target) return

    let disposed = false
    let heroVisible = true
    let metadataReady = video.readyState >= HTMLMediaElement.HAVE_METADATA
    let timer = 0
    let animationFrame = 0

    const clearScheduledWork = () => {
      window.clearTimeout(timer)
      window.cancelAnimationFrame(animationFrame)
      timer = 0
      animationFrame = 0
    }

    const setVideoTime = (time: number) => {
      if (!metadataReady) return
      try {
        video.currentTime = Math.min(AVATAR_DURATION, Math.max(0, time))
      } catch {
        // Safari can reject a seek while metadata is still settling. The next frame retries it.
      }
    }

    const canRun = () => !disposed && heroVisible && document.visibilityState !== 'hidden'

    const reset = () => {
      clearScheduledWork()
      setPhase('idle')
      setVideoTime(AVATAR_MIDPOINT)
    }

    const hold = (next: () => void, duration: number) => {
      timer = window.setTimeout(() => {
        if (canRun()) next()
      }, duration)
    }

    const turn = (nextPhase: MobileHeroPhase, from: number, to: number, complete: () => void) => {
      clearScheduledWork()
      setPhase(nextPhase)
      const startedAt = performance.now()

      const tick = (now: number) => {
        if (!canRun()) return
        const progress = Math.min(1, Math.max(0, (now - startedAt) / MOBILE_TURN_DURATION))
        if (!video.seeking || progress === 1) {
          setVideoTime(from + (to - from) * easeInOutCubic(progress))
        }
        if (progress < 1) {
          animationFrame = window.requestAnimationFrame(tick)
        } else {
          complete()
        }
      }

      animationFrame = window.requestAnimationFrame(tick)
    }

    const startLeft = () => {
      if (!canRun() || !metadataReady) return
      clearScheduledWork()
      setVideoTime(0)
      setPhase('left')
      hold(() => turn('turning-right', 0, AVATAR_DURATION, startRight), MOBILE_ICON_PHASE_DURATION)
    }

    const startRight = () => {
      setVideoTime(AVATAR_DURATION)
      setPhase('right')
      hold(() => turn('turning-center', AVATAR_DURATION, AVATAR_MIDPOINT, startCenter), MOBILE_ICON_PHASE_DURATION)
    }

    const startCenter = () => {
      setVideoTime(AVATAR_MIDPOINT)
      setPhase('center')
      hold(() => turn('turning-left', AVATAR_MIDPOINT, 0, startLeft), MOBILE_CENTER_PHASE_DURATION)
    }

    const synchronize = () => {
      if (!metadataReady || !canRun()) {
        reset()
        return
      }
      startLeft()
    }

    const onLoadedMetadata = () => {
      metadataReady = true
      startLeft()
    }

    const onVisibilityChange = () => {
      if (document.visibilityState === 'hidden') reset()
      else if (heroVisible && metadataReady) startLeft()
    }

    const observer = new IntersectionObserver(([entry]) => {
      heroVisible = Boolean(entry?.isIntersecting)
      if (heroVisible && metadataReady && document.visibilityState !== 'hidden') startLeft()
      else reset()
    }, { threshold: 0.12 })

    video.addEventListener('loadedmetadata', onLoadedMetadata)
    document.addEventListener('visibilitychange', onVisibilityChange)
    observer.observe(target)
    synchronize()

    return () => {
      disposed = true
      clearScheduledWork()
      observer.disconnect()
      video.removeEventListener('loadedmetadata', onLoadedMetadata)
      document.removeEventListener('visibilitychange', onVisibilityChange)
    }
  }, [enabled, targetRef, videoRef])

  return enabled ? phase : 'idle'
}
